<template>
  <tab-content
    title="Upload CSV"
    :before-change="beforeChange"
  >
    <b-form-group
      label="Data source"
      label-cols="3"
      label-size="lg"
      :disabled="loading"
    >
      <b-radio-group
        v-model="dataSource"
        buttons
        button-variant="outline-primary"
        class="btn-block"
      >
        <b-radio :value="SOURCE_CSV">
          CSV Upload
        </b-radio>
        <b-radio
          v-if="featureEnabled"
          :value="SOURCE_SNOWFLAKE"
        >
          Snowflake Import
        </b-radio>
      </b-radio-group>
    </b-form-group>

    <UploadCSV
      v-if="dataSource===SOURCE_CSV"
      :csv-file.sync="csvFile"
      :loading="loading"
    />
    <SnowflakeDetails
      v-if="dataSource===SOURCE_SNOWFLAKE && featureEnabled"
      v-bind.sync="snowflakeSource"
      :loading="loading"
      :v="$v"
      @error="onError"
    />
    <b-alert
      v-model="showWarning"
      dismissible
      variant="warning"
    >
      {{ warning }}
    </b-alert>
  </tab-content>
</template>

<script lang="ts">

import Component from 'vue-class-component'

import FeatureSetMixin from '@/mixins/FeatureSetMixin'
import UploadCSV from '@/components/admin/upload/UploadCSV.vue'
import SnowflakeDetails from '@/components/admin/upload/snowflake/SnowflakeDetails.vue'
import { required } from 'vuelidate/lib/validators'
import { postCSV, postSnowflakeDataVersionSource } from '@/api'
import { HttpError, TooManyRowsError } from '@/errors'
import { FeatureKeys, SnowflakeDataSourcePayload } from '@/types'
import { useUploadStore } from '@/store/admin/upload'

const SOURCE_SNOWFLAKE = 'snowflake'
const SOURCE_CSV = 'csv'

const components = {
  UploadCSV,
  SnowflakeDetails,
}

const props = {
  loading: {
    type: Boolean,
    default: false,
  },
}

@Component({ components })
export default class UploadData extends FeatureSetMixin.extend({ props }) {
  dataSource = SOURCE_CSV
  SOURCE_CSV = SOURCE_CSV
  SOURCE_SNOWFLAKE = SOURCE_SNOWFLAKE
  csvFile: Blob | null = null
  snowflakeSource = {
    username: '',
    password: '',
    account: '',
    warehouse: '',
    db: '',
    schema: '',
    tableview: '',
  }

  featureKey = FeatureKeys.SNOWFLAKE
  featureEnabled!: boolean

  showWarning = false
  warning = ''
  upload = useUploadStore()

  validations () {
    return {
      snowflakeSource: {
        username: {
          required,
        },
        password: {
          required,
        },
        account: {
          required,
        },
        db: {
          required,
        },
        schema: {
          required,
        },
        tableview: {
          required,
        },
      },
    }
  }

  onError (warning: string) {
    this.showWarning = true
    this.warning = warning
  }

  async beforeChange () {
    this.$v.$touch()
    if (this.dataSource === SOURCE_CSV) {
      return this.handleCSVSource()
    } else if (this.dataSource === SOURCE_SNOWFLAKE && !this.$v.$invalid) {
      return this.handleSnowflakeSource()
    }
    return false
  }

  async handleCSVSource () {
    try {
      if (this.csvFile) await this.upload.processCsv(this.csvFile)
      await postCSV(this.upload.csvJson)
      return true
    } catch (e) {
      if (e instanceof TooManyRowsError) {
        this.onError(e.toString())
        return false
      } else {
        throw e
      }
    }
  }

  async handleSnowflakeSource () {
    try {
      const {
        username,
        account,
        password,
        warehouse,
        db,
        schema,
        tableview,
      } = this.snowflakeSource
      const data = await this.upload.getSnowflakeData(this.snowflakeSource)
      await postSnowflakeDataVersionSource({
        username,
        account,
        password,
        warehouse,
        database: db,
        schema,
        table: tableview,
      } as SnowflakeDataSourcePayload)
      await this.upload.processArrayJSONObj(data)
    } catch (e) {
      if (e instanceof HttpError && e.status === 400) {
        this.onError(await e.getJson())
        return false
      } else {
        throw e
      }
    }
    await postCSV(this.upload.csvJson)
    return true
  }
}

</script>
